import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Page, Card, Tabs, InlineStack, BlockStack, Text, Box, Badge, Banner,
  Button, Spinner, List, Toast,
} from '@shopify/polaris';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getImport, getImportRows, applyImport, approveInvalidRow, approveAllInvalid,
  type Disposition, type ImportRow, type ImportJob, type PriceSyncSummary,
} from '../api/imports';

const DISPOSITIONS: Disposition[] = ['new', 'update', 'duplicate', 'invalid'];

export function ImportDetail() {
  const { id } = useParams<{ id: string }>();
  const numericId = id ? parseInt(id, 10) : null;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Disposition>('new');
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['import', numericId],
    queryFn: () => getImport(numericId!),
    enabled: numericId !== null,
    refetchInterval: (query) => {
      const s = (query.state.data as any)?.job?.status;
      return s === 'pending' || s === 'processing' ? 2000 : false;
    },
  });

  const job = data?.job;

  const { data: rowData, isLoading: rowsLoading } = useQuery({
    queryKey: ['import-rows', numericId, tab],
    queryFn: () => getImportRows(numericId!, { disposition: tab, limit: 100 }),
    enabled: numericId !== null && !!job && job.status !== 'pending' && job.status !== 'processing',
  });

  const apply = useMutation({
    mutationFn: () => applyImport(numericId!),
    onSuccess: (r) => {
      setToast(`Applied: +${r.inserted} new, ~${r.updated} updated, ${r.skipped} skipped`);
      qc.invalidateQueries({ queryKey: ['import', numericId] });
      qc.invalidateQueries({ queryKey: ['imports'] });
    },
  });

  // Force-approve a single invalid row — flips it to 'new' so the next
  // Apply import pass tries to insert it, skipping the validator rules
  // that rejected it originally. Use for "this is a real bolt pattern, the
  // alias table just doesn't know it yet" situations.
  const approveOne = useMutation({
    mutationFn: (rowId: number) => approveInvalidRow(numericId!, rowId),
    onSuccess: () => {
      setToast('Row approved — will insert on next Apply');
      qc.invalidateQueries({ queryKey: ['import-rows', numericId] });
      qc.invalidateQueries({ queryKey: ['import', numericId] });
    },
    onError: (err: Error) => setToast(`Approve failed: ${err.message}`),
  });

  const approveAll = useMutation({
    mutationFn: () => approveAllInvalid(numericId!),
    onSuccess: (r) => {
      setToast(`Approved ${r.approved} invalid rows`);
      qc.invalidateQueries({ queryKey: ['import-rows', numericId] });
      qc.invalidateQueries({ queryKey: ['import', numericId] });
    },
  });

  if (isLoading) return <Page title="Import"><Spinner /></Page>;
  if (error) return <Page title="Import"><Banner tone="critical">{(error as Error).message}</Banner></Page>;
  if (!job) return <Page title="Import"><Banner>Not found</Banner></Page>;

  const processing = job.status === 'pending' || job.status === 'processing';
  const applied = job.status === 'applied';

  return (
    <>
      <Page
        // Was: "Import #80" / "wheels · vendor-pull" — confusingly hid the
        // vendor (ATD vs WheelPros) in a button at the bottom of the page,
        // making it easy to misread an ATD wheel pull as a Wheel Pros one.
        // Now: vendor + entity in both title and subtitle, no ambiguity.
        title={`${job.source_guide ?? 'Vendor'} ${job.source ?? 'wheel'} import #${job.id}`}
        subtitle={`Vendor: ${job.source_guide ?? '(unknown)'} · Entity: ${job.source ?? job.source_guide ?? '(unknown)'} · File: ${job.filename}`}
        backAction={{ content: 'Imports', onAction: () => navigate('/imports') }}
        primaryAction={
          job.status === 'preview' ? {
            content: 'Apply import',
            loading: apply.isPending,
            disabled: job.row_count === 0 || (job.new_count + job.update_count === 0),
            onAction: () => { if (confirm(`Apply ${job.new_count} new + ${job.update_count} updates?`)) apply.mutate(); },
          } : undefined
        }
      >
        <BlockStack gap="400">
          {processing && <Banner tone="info"><InlineStack gap="200"><Spinner size="small" /><span>Worker is processing this job…</span></InlineStack></Banner>}
          {job.status === 'failed' && <Banner tone="critical">Processing failed. Check CloudWatch logs for the worker.</Banner>}
          {applied && <Banner tone="success">Applied at {new Date(job.applied_at!).toLocaleString()}. {job.new_count + job.update_count} rows written.</Banner>}
          {apply.error && <Banner tone="critical">Apply failed: {(apply.error as Error).message}</Banner>}

          <Card>
            <InlineStack gap="400" wrap>
              <Stat label="Rows" value={job.row_count} />
              <Stat label="New" value={job.new_count} tone="success" />
              <Stat label="Update" value={job.update_count} tone="info" />
              <Stat label="Duplicate" value={job.duplicate_count} tone="attention" />
              <Stat label="Invalid" value={job.invalid_count} tone={job.invalid_count > 0 ? 'critical' : undefined} />
            </InlineStack>
          </Card>

          {!processing && (job.filename === 'map-shopify-sync' || job.filename === 'price-shopify-sync') && (
            <PriceSyncCard job={job} />
          )}

          {!processing && job.filename === 'vendor-pull' && (
            <Card>
              <BlockStack gap="300">
                <Text as="h3" variant="headingMd">Vendor pull summary</Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Vendor-pull jobs write directly to <code>wheel_products</code> via upsert
                  — they don't land rows in the staging table the disposition tabs read from.
                  The counts above are the actual catalog-level effect (insert / update).
                </Text>
                <InlineStack gap="200" wrap>
                  <Badge tone="success">{`${job.new_count} new SKUs added to catalog`}</Badge>
                  <Badge tone="info">{`${job.update_count} existing SKUs updated`}</Badge>
                  {job.invalid_count > 0 && (
                    <Badge tone="critical">{`${job.invalid_count} skipped (no resolvable bolt pattern)`}</Badge>
                  )}
                </InlineStack>
                <Box paddingBlockStart="200">
                  <Button onClick={() => window.location.href = `/wheels?source_guide=${encodeURIComponent(job.source_guide ?? '')}`}>
                    {`View ${job.source_guide ?? 'vendor'} wheels in catalog →`}
                  </Button>
                </Box>
              </BlockStack>
            </Card>
          )}

          {!processing && job.filename !== 'vendor-pull'
            && job.filename !== 'map-shopify-sync' && job.filename !== 'price-shopify-sync' && (
            <Card padding="0">
              <Box padding="400" borderBlockEndWidth="025" borderColor="border">
                <InlineStack align="space-between" blockAlign="center" wrap>
                  <Tabs
                    selected={DISPOSITIONS.indexOf(tab)}
                    onSelect={(i) => setTab(DISPOSITIONS[i])}
                    tabs={DISPOSITIONS.map((d) => ({
                      id: d, content: `${capitalize(d)} (${job[`${d}_count`]})`, panelID: d,
                    }))}
                  />
                  {tab === 'invalid' && job.invalid_count > 0 && job.status === 'preview' && (
                    <Button
                      tone="critical"
                      loading={approveAll.isPending}
                      onClick={() => {
                        if (!confirm(`Force-approve all ${job.invalid_count} invalid rows? They'll be treated as 'new' on the next Apply — DB-level constraints still enforce basic sanity, but anything the validator flagged will be retried.`)) return;
                        approveAll.mutate();
                      }}
                    >{`Approve all ${job.invalid_count} invalid`}</Button>
                  )}
                </InlineStack>
              </Box>
              <Box padding="400">
                {rowsLoading
                  ? <InlineStack align="center"><Spinner /></InlineStack>
                  : (rowData?.data ?? []).length === 0
                    ? <Text as="p" tone="subdued" alignment="center">No {tab} rows.</Text>
                    : <BlockStack gap="300">
                        {(rowData?.data ?? []).map((r) => (
                          <RowCard
                            key={r.id}
                            row={r}
                            onApprove={r.disposition === 'invalid' && job.status === 'preview'
                              ? () => approveOne.mutate(r.id)
                              : undefined}
                            approving={approveOne.isPending && approveOne.variables === r.id}
                          />
                        ))}
                      </BlockStack>}
              </Box>
            </Card>
          )}
        </BlockStack>
        {toast && <Toast content={toast} onDismiss={() => setToast(null)} />}
      </Page>
    </>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'success' | 'info' | 'critical' | 'attention' }) {
  return (
    <Box minWidth="120px">
      <BlockStack gap="100">
        <Text as="p" variant="bodySm" tone="subdued">{label}</Text>
        {tone
          ? <Badge tone={tone}>{value.toLocaleString()}</Badge>
          : <Text as="p" variant="headingLg">{value.toLocaleString()}</Text>}
      </BlockStack>
    </Box>
  );
}

function RowCard({ row, onApprove, approving }: {
  row: ImportRow;
  onApprove?: () => void;
  approving?: boolean;
}) {
  const hasToleranceWarning = row.warnings.some((w) => typeof w === 'string' && w.includes('tolerance'));
  return (
    <Box padding="300" borderWidth="025" borderColor={row.disposition === 'invalid' ? 'border-critical' : 'border'} borderRadius="200">
      <BlockStack gap="200">
        <InlineStack gap="200" blockAlign="center" align="space-between">
          <InlineStack gap="200" blockAlign="center">
            <Text as="span" variant="bodySm" tone="subdued">row {row.row_index}</Text>
            {row.target_id && <Badge>{`target #${row.target_id}`}</Badge>}
            {hasToleranceWarning && <Badge tone="warning">Tolerance-snapped BP</Badge>}
            {row.applied && <Badge tone="success">applied</Badge>}
          </InlineStack>
          {onApprove && (
            <Button size="slim" tone="critical" loading={approving} onClick={onApprove}>
              Approve as valid
            </Button>
          )}
        </InlineStack>
        {row.errors.length > 0 && (
          <Box padding="200" background="bg-surface-critical" borderRadius="100">
            <Text as="p" variant="bodySm" fontWeight="semibold">Errors</Text>
            <List type="bullet">{row.errors.map((e, i) => <List.Item key={i}>{e}</List.Item>)}</List>
          </Box>
        )}
        {row.warnings.length > 0 && (
          <Box padding="200" background="bg-surface-warning" borderRadius="100">
            <Text as="p" variant="bodySm" fontWeight="semibold">Warnings</Text>
            <List type="bullet">{row.warnings.map((w, i) => <List.Item key={i}>{w}</List.Item>)}</List>
          </Box>
        )}
        <details>
          <summary style={{ cursor: 'pointer', fontSize: 12, color: '#616161' }}>Raw + parsed payloads</summary>
          <InlineStack gap="400" align="start" wrap>
            <Box minWidth="280px">
              <Text as="p" variant="bodySm" tone="subdued">Raw</Text>
              <code style={{ fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {JSON.stringify(row.raw_payload, null, 2)}
              </code>
            </Box>
            <Box minWidth="280px">
              <Text as="p" variant="bodySm" tone="subdued">Parsed</Text>
              <code style={{ fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {row.parsed_payload ? JSON.stringify(row.parsed_payload, null, 2) : '(null)'}
              </code>
            </Box>
          </InlineStack>
        </details>
      </BlockStack>
    </Box>
  );
}

function capitalize(s: string): string { return s.charAt(0).toUpperCase() + s.slice(1); }

// Price-sync detail card. Reads metadata.price_shopify_sync (or legacy
// metadata.map_shopify_sync) and surfaces it as a short readable summary.
// Goal: one-glance answer to "is this sync run healthy or not?"
function PriceSyncCard({ job }: { job: ImportJob }) {
  const meta = (job.metadata ?? {}) as Record<string, unknown>;
  const s = (meta.price_shopify_sync ?? meta.map_shopify_sync) as PriceSyncSummary | undefined;
  const health = job.health ?? s?.health ?? null;

  // Banner first — single, plain-English summary of what happened.
  let banner: { tone: 'success' | 'info' | 'warning' | 'critical'; text: string } | null = null;
  if (health === 'healthy')              banner = { tone: 'success',  text: 'Prices synced cleanly.' };
  else if (health === 'no_drift')        banner = { tone: 'info',     text: 'Nothing to do — Shopify already matches the catalog.' };
  else if (health === 'partial')         banner = { tone: 'warning',  text: 'Partial run — some prices synced, some failed.' };
  else if (health === 'failed')          banner = { tone: 'critical', text: 'Sync failed. See counts and CloudWatch for details.' };
  else if (health === 'scanned_zero')    banner = { tone: 'critical', text: 'Scanned 0 rows. Sync did not actually look at any products — likely a config or filter issue.' };
  else if (health === 'no_variants_returned') banner = { tone: 'critical', text: 'Shopify returned nothing for the scanned variants — likely API throttling or stale variant IDs.' };

  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h3" variant="headingMd">Price sync</Text>
        {banner && <Banner tone={banner.tone}>{banner.text}</Banner>}
        {!s ? (
          <Text as="p" variant="bodySm" tone="subdued">
            No structured summary stored for this run (older job — only the row counts above are available).
          </Text>
        ) : (
          <BlockStack gap="200">
            <InlineStack gap="400" wrap>
              <Stat label="Scanned"  value={s.scanned} />
              <Stat label="In sync"  value={s.unchanged} tone="success" />
              <Stat label="Drift"    value={s.drift_detected} tone={s.drift_detected > 0 ? 'attention' : undefined} />
              <Stat label={s.dry_run ? 'Would apply' : 'Applied'} value={s.applied} tone="info" />
              <Stat label="Failed"   value={s.failed} tone={s.failed > 0 ? 'critical' : undefined} />
              <Stat label="Read err" value={s.read_errors} tone={s.read_errors > 0 ? 'critical' : undefined} />
              <Stat label="Unsafe"   value={s.unsafe_skipped} tone={s.unsafe_skipped > 0 ? 'attention' : undefined} />
              <Stat label="MAP floor" value={s.floored_to_map_count} />
            </InlineStack>
            {s.by_product_type && (s.by_product_type.wheels.scanned > 0 || s.by_product_type.tires.scanned > 0) && (
              <Text as="p" variant="bodySm" tone="subdued">
                Wheels: {s.by_product_type.wheels.scanned} scanned · {s.by_product_type.wheels.drift} drift · {s.by_product_type.wheels.applied} synced
                {' · '}
                Tires: {s.by_product_type.tires.scanned} scanned · {s.by_product_type.tires.drift} drift · {s.by_product_type.tires.applied} synced
              </Text>
            )}
            {s.dry_run && (
              <Text as="p" variant="bodySm" tone="subdued">Dry run — Shopify was not modified.</Text>
            )}
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  );
}

